import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        if (!formData.name) {
          toast.error("Please enter your name");
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password);
        toast.success("Account created!");
      }
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-5xl font-bold text-[#39FF14] neon-text heading">GCSR</span>
          <h1 className="mt-6 text-3xl heading text-[#EDEDED]">
            {isLogin ? "Welcome Back" : "Join the Crew"}
          </h1>
          <p className="mt-2 text-[#888]">
            {isLogin ? "Sign in to your account" : "Create your GCSR account"}
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#0A0A0A] border border-[#222] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-[#888] label-text">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-2 gcsr-input w-full"
                  placeholder="Your name"
                  data-testid="auth-name"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-[#888] label-text">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-2 gcsr-input w-full"
                placeholder="your@email.com"
                required
                data-testid="auth-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#888] label-text">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="gcsr-input w-full pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="auth-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-[#EDEDED]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase tracking-wider disabled:opacity-50"
              data-testid="auth-submit"
            >
              <span>{loading ? "Loading..." : isLogin ? "Sign In" : "Create Account"}</span>
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#888]">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-[#39FF14] hover:underline label-text"
                data-testid="auth-toggle"
              >
                {isLogin ? "Sign Up" : "Sign In"}
              </button>
            </p>
          </div>
        </div>

        {/* Social Proof */}
        <p className="mt-8 text-center text-[#444] text-sm">
          Join thousands of street racing enthusiasts
        </p>
      </div>
    </div>
  );
}
